import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { map, catchError, switchMap, tap, filter } from 'rxjs/operators';
import { of } from 'rxjs';

import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { AnalyticsService } from '../../services/analytics.service';
import { userNeedsProfileCompletion } from '../../utils/user-needs-profile-completion';
import * as AuthActions from './auth.actions';

/**
 * Effects semplificati - delegano tutta la logica al AuthService
 */
@Injectable()
export class AuthEffects implements OnInitEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private store = inject(Store);
  private router = inject(Router);
  private analyticsService = inject(AnalyticsService);

  /**
   * Inizializza l'effect che ascolta i cambiamenti di stato auth da Firebase
   */
  ngrxOnInitEffects(): Action {
    return AuthActions.initializeAuth();
  }

  /**
   * Effect che avvia il listener Firebase quando l'app viene inizializzata
   * Ora ascolta i dati Firebase e innesca il caricamento dei dati backend
   */
  initAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initializeAuth),
      switchMap(() => this.authService.firebaseUser$),
      map(firebaseUser => AuthActions.authStateChanged({ firebaseUser }))
    )
  );

  /**
   * Carica i dati backend quando Firebase segnala un utente autenticato
   */
  loadBackendDataOnAuthChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.authStateChanged),
      filter(({ firebaseUser }) => firebaseUser !== null),
      map(() => AuthActions.loadBackendUserData())
    )
  );

  /**
   * Carica i dati backend dopo login classico success
   */
  loadBackendDataAfterAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      map(() => AuthActions.loadBackendUserData())
    )
  );

  /**
   * Dopo registrazione success (NUOVO UTENTE)
   * Crea l'utente nel database backend
   */
  createUserAfterRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registerSuccess),
      map(({ firebaseUser, newsletterDailyUpdatesConsent }) =>
        AuthActions.createBackendUser({ firebaseUser, newsletterDailyUpdatesConsent }),
      ),
    )
  );

  /**
   * Dopo login Google success con NUOVO UTENTE (primo accesso)
   * Crea l'utente nel database backend
   */
  createUserAfterGoogleLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithGoogleSuccess),
      filter(({ isNewUser }) => isNewUser === true),
      map(({ firebaseUser }) =>
        AuthActions.createBackendUser({ firebaseUser, isGoogleRegistration: true }),
      ),
    )
  );

  /**
   * Dopo login Google success con UTENTE ESISTENTE
   * Carica i dati dal database backend
   */
  loadUserAfterGoogleLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithGoogleSuccess),
      filter(({ isNewUser }) => isNewUser === false),
      map(() => AuthActions.loadBackendUserData())
    )
  );

  /**
   * Effettua la chiamata a user/me per caricare i dati completi dell'utente dal backend
   */
  loadBackendUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadBackendUserData),
      switchMap(() =>
        this.userService.getCurrentUser().pipe(
          map(user => {
            // Imposta l'ID utente e il Firebase UID in Analytics per il tracking
            if (user?.id) {
              this.analyticsService.setUserId(user.id);
              // Imposta anche il Firebase UID come proprietà utente personalizzata
              // per poter identificare gli utenti in analytics
              if (user.firebaseUid) {
                this.analyticsService.setUserProperties({
                  firebase_uid: user.firebaseUid,
                  user_email: user.email,
                  user_name: `${user.name} ${user.surname}`
                });
              }
            }
            return AuthActions.loadBackendUserDataSuccess({ user });
          }),
          catchError((error) => {
            // Estrai il messaggio di errore, mantenendo quello del backend se disponibile
            let errorMessage = 'Errore nel caricamento dei dati utente';
            if (error?.message) {
              errorMessage = error.message;
            } else if (error?.error?.message) {
              errorMessage = error.error.message;
            }
            
            return of(AuthActions.loadBackendUserDataFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Crea/registra l'utente nel database backend
   * Viene chiamato dopo login Google per assicurarsi che l'utente esista nel DB
   * 
   * NOTA: Idealmente il backend dovrebbe implementare un comportamento "upsert":
   * - Se utente non esiste → crea e ritorna i dati
   * - Se utente esiste → aggiorna (se necessario) e ritorna i dati esistenti
   * Questo semplificherebbe il flusso evitando la gestione dell'errore 409
   */
  createBackendUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.createBackendUser),
      switchMap(({ firebaseUser, newsletterDailyUpdatesConsent, isGoogleRegistration }) => {
        const createUserDto = {
          ...this.authService.createUserDtoFromFirebase(firebaseUser),
          ...(newsletterDailyUpdatesConsent === true ? { newsletterDailyUpdatesConsent: true } : {}),
          ...(isGoogleRegistration === true ? { profileCompletionPending: true } : {}),
        };
        return this.userService.createUser(createUserDto).pipe(
          map(user => {
            // Imposta l'ID utente e il Firebase UID in Analytics per il tracking
            if (user?.id) {
              this.analyticsService.setUserId(user.id);
              // Imposta anche il Firebase UID come proprietà utente personalizzata
              // per poter identificare gli utenti in analytics
              if (user.firebaseUid) {
                this.analyticsService.setUserProperties({
                  firebase_uid: user.firebaseUid,
                  user_email: user.email,
                  user_name: `${user.name} ${user.surname}`
                });
              }
            }
            return AuthActions.createBackendUserSuccess({ user });
          }),
          catchError((error) => {
            // Se l'utente esiste già (409), carica i dati esistenti
            // Poi l'effect loadBackendUserDataAfterConflict$ gestirà il redirect
            if (error?.status === 409) {
              return this.userService.getCurrentUser().pipe(
                map(user => {
                  // Imposta l'ID utente e il Firebase UID in Analytics per il tracking
                  if (user?.id) {
                    this.analyticsService.setUserId(user.id);
                    // Imposta anche il Firebase UID come proprietà utente personalizzata
                    // per poter identificare gli utenti in analytics
                    if (user.firebaseUid) {
                      this.analyticsService.setUserProperties({
                        firebase_uid: user.firebaseUid,
                        user_email: user.email,
                        user_name: `${user.name} ${user.surname}`
                      });
                    }
                  }
                  return AuthActions.createBackendUserSuccess({ user });
                }),
                catchError((loadError) => {
                  const errorMessage = loadError?.message || 'Errore nel caricamento dei dati utente';
                  return of(AuthActions.createBackendUserFailure({ error: errorMessage }));
                })
              );
            }
            
            const errorMessage = error?.message || 'Errore nella registrazione dell\'utente';
            return of(AuthActions.createBackendUserFailure({ error: errorMessage }));
          })
        );
      })
    )
  );

  /**
   * Effect per il login
   * Ora restituisce solo i dati Firebase, il backend viene caricato dopo
   */
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ credentials }) =>
        this.authService.login(credentials).pipe(
          map(firebaseUser => AuthActions.loginSuccess({ firebaseUser })),
          catchError((error) => {
            const errorMessage = error?.message || 'Errore durante il login';
            return of(AuthActions.loginFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Effect per la registrazione
   * Ora restituisce solo i dati Firebase, il backend viene caricato dopo
   */
  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.register),
      switchMap(({ registration }) =>
        this.authService.register(registration).pipe(
          map(firebaseUser =>
            AuthActions.registerSuccess({
              firebaseUser,
              newsletterDailyUpdatesConsent:
                registration.newsletterDailyUpdatesConsent === true ? true : undefined,
            }),
          ),
          catchError((error) => {
            const errorMessage = error?.message || 'Errore durante la registrazione';
            return of(AuthActions.registerFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Effect per il login con Google
   * Apre un popup per l'autenticazione Google
   * Ritorna i dati utente + informazione se è nuovo utente
   */
  loginWithGoogle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithGoogle),
      switchMap(() =>
        this.authService.loginWithGoogle().pipe(
          map(({ firebaseUser, isNewUser }) => {
            // Traccia evento Analytics
            if (isNewUser) {
              this.analyticsService.logSignUp('google');
            } else {
              this.analyticsService.logLogin('google');
            }
            return AuthActions.loginWithGoogleSuccess({ firebaseUser, isNewUser });
          }),
          catchError((error) => {
            const errorMessage = error?.message || 'Errore durante il login con Google';
            return of(AuthActions.loginWithGoogleFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Effect per il logout
   */
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      switchMap(() =>
        this.authService.logout().pipe(
          map(() => {
            // Rimuovi l'ID utente e le proprietà da Analytics
            this.analyticsService.setUserId(null);
            this.analyticsService.setUserProperties({});
            return AuthActions.logoutSuccess();
          }),
          catchError((error) => {
            const errorMessage = error?.message || 'Errore durante il logout';
            return of(AuthActions.logoutFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Se l'utente è bloccato, disconnetti automaticamente da Firebase
   * Mantiene l'errore nello stato per mostrarlo nella pagina di login
   */
  handleBlockedUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadBackendUserDataFailure),
      filter(({ error }) => error?.includes('bloccato') || error?.includes('blocked')),
      switchMap(({ error }) => {
        // Salva l'errore prima di fare logout
        const blockedError = error;
        return this.authService.logout().pipe(
          map(() => AuthActions.logoutSuccessWithError({ error: blockedError })),
          catchError(() => of(AuthActions.logoutSuccessWithError({ error: blockedError })))
        );
      })
    )
  );

  /**
   * Redirect dopo logout success - fa un refresh completo della pagina
   */
  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess, AuthActions.logoutSuccessWithError),
        tap(() => {
          // Fai un refresh completo della pagina dopo il logout
          if (typeof window !== 'undefined') {
            window.location.href = '/';
          } else {
            this.router.navigate(['/login']);
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Redirect dopo login success (solo login classico)
   * Per Google, il redirect avviene dopo createBackendUserSuccess
   */
  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(() => {
          // Redirect alla home dopo login classico
          this.router.navigate(['/']);
        })
      ),
    { dispatch: false }
  );

  /**
   * Redirect dopo register success non necessario
   * Il redirect avviene dopo createBackendUserSuccess per la registrazione normale
   */

  /**
   * Dopo caricamento utente backend: completa profilo Google se necessario, altrimenti home/login
   */
  postAuthNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loadBackendUserDataSuccess, AuthActions.createBackendUserSuccess),
        tap(({ user }) => {
          const pending = userNeedsProfileCompletion(user);
          const path = this.router.url.split('?')[0];
          if (pending) {
            if (!path.startsWith('/completa-registrazione')) {
              this.router.navigate(['/completa-registrazione']);
            }
          } else {
            if (path.startsWith('/completa-registrazione')) {
              this.router.navigate(['/']);
            } else if (path.startsWith('/login')) {
              this.router.navigate(['/']);
            }
          }
        }),
      ),
    { dispatch: false },
  );
}
